import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';

import { toastError, toastSuccess } from '@components/Toast';
import CommonImage from '@components/CommonImage';
import CustomDropdown from '@components/CustomDropdown';
import ModalContainer from '@components/modals/ModalContainer';

import OrderCancelBottomBar from '@features/order-cancel/OrderCancelBottomBar';
import PlaceOrderConfirmation from '@features/order/components/orderInfo/PlaceOrderConfirmation';
import DesignsByCollection from '@features/order/DesignsByCollection';
import { getGroupPoData } from '@features/order/utils/projectUtil';
import ColorSizeBreakDownTable from '@features/order/components/orderInfo/ColorSizeBreakDownTable';
import OrderCancelInfo from '@features/order-cancel/OrderCancelInfo';
import EtdUpdateLog from '@features/order/components/orderInfo/EtdUpdateLog';
import EtdUpdateTimeline from '@features/order/components/orderInfo/EtdUpdateTimeline';
import {
	useCreateProgramTypeMutation,
	useLazyGetAllProgramTypesQuery,
	useUpdateProgramTypeMutation
} from '@features/order/api/OrderDetailsApi';

import { fetchOrderDetailsInfo } from '@store/orders/actions';
import { closeLoader, openLoader } from '@store/actions/LoaderAction';
import { clearDesignViewModalInfo, setDesignViewModalInfo } from '@store/actions/DesignViewModalAction';
import { getOrderDetailsInfo } from '@store/reducers';

import { fetchEnumValues } from '@services/commonService';

import ApiClient from '@lib/apiClient';

import {
	isAdmin,
	packTag,
	renderSetImages,
	setTag,
	splitTag,
	STATIC_SET_CONSTANTS,
	validateNumber
} from '@utils/Util';

import { BASE_URL } from '@/constants/constant';

const OrderStyles = ({
	orderId,
	isDrafted,
	productRefNumber,
	hideStatusType,
	addNewStyle,
	poItemIdentifier,
	orderCancelInfo,
	getOrderCancelInfo,
	pOSegmentationType,
	setOpenOrderDesignModal,
	stylesCount
}) => {
	const dispatch = useDispatch();
	const [orderStyles, setOrderStyles] = useState([]);
	const [isOverflow, setIsOverflow] = useState(false);
	const [, setLoading] = useState(false);
	const [groupData, setGroupData] = useState(null);
	const [, setSeasons] = useState([]);
	const [, setSetPackStyleFlag] = useState([]);
	const [singleSetPackStyle] = useState('');
	const [designIdList, setDesignIdList] = useState([]);
	const [, setRemovedColorIds] = useState([]);
	const [, setProductWiseColorIds] = useState({});
	const [selectedRFQIds, setSelectedRFQIds] = useState([]);
	const [error, setError] = useState('');
	const [errorDesign, setErrorDesign] = useState({});
	const [show, setShow] = useState(false);
	const [isPlacingOrder, setIsPlacingOrder] = useState(false);
	const [openStyleModal, setOpenStyleModal] = useState(false);
	const [, setOpenAccordion] = useState([]);
	const [errors, setErrors] = useState({});
	const buttonRef = useRef();
	const inputRefs = useRef([]);
	let orderDetailsInfo = useSelector(getOrderDetailsInfo);
	const [selectedPo, setSelectedPo] = useState(null);
	const [programTypeOptionList, setProgramTypeOptionList] = useState([]);
	const [showProgramTypeModal, setShowProgramTypeModal] = useState(false);
	const [selectedProgramType, setSelectedProgramType] = useState(null);
	const [programTypeName, setProgramTypeName] = useState('');
	const [isProgramTypeSubmitting, setIsProgramTypeSubmitting] = useState(false);
	const [programTypeErrors, setProgramTypeErrors] = useState({});
	const [fetchProgramTypeTrigger] = useLazyGetAllProgramTypesQuery();
	const [createProgramType] = useCreateProgramTypeMutation();
	const [updateProgramType] = useUpdateProgramTypeMutation();

	const programTypeOptions = useMemo(() => {
		if (!Array.isArray(programTypeOptionList)) {
			return [];
		}
		return programTypeOptionList
			.map((item) => ({
				id: item?.id,
				name: item?.name
			}))
			.filter((item) => item.id && item.name);
	}, [programTypeOptionList]);

	useEffect(() => {
		const allProductRefNumbers = groupData?.map((item) => item.productReferenceNumber);
		setOpenAccordion(allProductRefNumbers);
	}, [groupData]);

	const getProductIds = (idList) => {
		return idList?.map((item) => item.productId);
	};

	const productIds = useMemo(() => getProductIds(orderStyles), [orderStyles]);

	useEffect(() => {
		if (productRefNumber) {
			const element = document.getElementById(productRefNumber);
			if (element) {
				element.scrollIntoView({ behavior: 'smooth', block: 'center' });
			}
		}
	}, [productRefNumber]);

	useEffect(() => {
		if (orderStyles) {
			let ids = orderStyles.map((item) => item.productId);
			setDesignIdList(ids);
			if (!isDrafted) {
				let quotationColorIds = [];
				let productColorIds = [];
				orderStyles
					.map((styleItem) => styleItem.colorWiseSizeQuantityPairList)
					.forEach((colorItem) => {
						if (colorItem) {
							quotationColorIds.push(...colorItem.map((singleItem) => singleItem.id));
						}
					});
				orderStyles
					.map((styleItem) => styleItem?.productColorIds)
					.forEach((colorIds) => {
						if (colorIds) {
							productColorIds.push(...colorIds);
						}
					});

				let removedColorIds = productColorIds
					.filter((id) => !quotationColorIds.includes(id))
					.map((id) => parseInt(id));
				setRemovedColorIds(removedColorIds);
			} else {
				let productWiseColorIds = {};
				orderStyles.map((styleItem) => {
					productWiseColorIds[styleItem.productId] = styleItem.colorWiseSizeQuantityPairList?.map(
						(colorId) => colorId.id
					);
				});
				setProductWiseColorIds(productWiseColorIds);
			}
		}
	}, [orderStyles]);

	useEffect(() => {
		setGroupData(getGroupPoData(filterStylesData()));
	}, [orderStyles, hideStatusType]);

	useEffect(() => {
		getOrderQuotes(orderId);
	}, [orderId]);

	const getProgramTypePayload = (response) => {
		if (Array.isArray(response)) return response;
		if (Array.isArray(response?.data)) return response.data;
		return [];
	};

	const fetchProgramTypes = async () => {
		try {
			const response = await fetchProgramTypeTrigger().unwrap();
			setProgramTypeOptionList(getProgramTypePayload(response));
		} catch (error) {
			toastError(error?.data?.message || 'Failed to fetch program types.');
		}
	};

	useEffect(() => {
		fetchProgramTypes();
	}, []);

	const onSave = () => {
		const missingProgramTypes = {};
		orderStyles.forEach((item) => {
			if (!item.programTypeId && !item.programType?.id) {
				missingProgramTypes[item.id] = 'Program type is mandatory';
			}
		});
		if (Object.keys(missingProgramTypes).length > 0) {
			setProgramTypeErrors(missingProgramTypes);
			return;
		}
		setProgramTypeErrors({});
		setShow(true);
	};

	const createOrder = async () => {
		setIsPlacingOrder(true);
		try {
			setLoading(true);
			const { data } = await ApiClient.POST(
				'submitDraftedOrder',
				{},
				`${orderId}/submit-drafted-order`
			);
			setIsPlacingOrder(false);
			if (data?.success) {
				toastSuccess(data.message);
				dispatch(fetchOrderDetailsInfo(orderId));
				setShow(false);
			} else {
				toastError('Unexpected response format.');
			}
		} catch (error) {
			setIsPlacingOrder(false);
			const errorMessage = error?.response?.data?.message || 'Request was unsuccessful.';
			toastError(errorMessage);
		} finally {
			setIsPlacingOrder(false);
			setLoading(false);
		}
	};
	useEffect(() => {
		(async () => {
			try {
				await fetchEnumValues('info.nitex.app.product.SetPackStyleFlag', setSetPackStyleFlag);
				await fetchEnumValues('info.nitex.app.materials.Season', setSeasons);
			} catch {}
		})();
	}, []);

	useEffect(() => {
		if (openStyleModal) {
			buttonRef.current.click();
		}
	}, [openStyleModal]);

	const getOrderQuotes = async (orderId) => {
		setLoading(true);
		let url = 'getOrderQuotes';
		const search = window.location.search;
		const params = new URLSearchParams(search);
		const requestId = params.get('requestId');
		if (requestId !== null) {
			url = `${BASE_URL}/order/cancel/request/${requestId}/quotes`;
		}

		await ApiClient.GET(url, requestId === null ? +orderId : '', requestId !== null)
			.then(({ data }) => {
				setOrderStyles(
					data.map((item) => {
						return {
							...item,
							referenceNumber: item?.productReferenceNumber || '',
							priceType: item?.priceType || orderDetailsInfo?.incoterms
						};
					})
				);

				setLoading(false);
			})
			.catch(({ response }) => {
				setLoading(false);
				if (response && response.data && response.data.message) {
					toastError(response.data.message);
				} else {
					toastError('Something went wrong! Please try again.');
				}
			});
	};

	const onSetEditIndex = (id, editable) => {
		setGroupData((prevGroupPoData) =>
			prevGroupPoData.map((group) => ({
				...group,
				poData: group.poData.map((poItem) => {
					if (poItem.id === id) {
						return { ...poItem, isEdit: editable };
					}
					return poItem.hasOwnProperty('isEdit') ? poItem : { ...poItem, isEdit: false };
				})
			}))
		);

		const updatedArray = orderStyles.map((item) => {
			if (item.id === id) {
				const updatedItem = {
					...item,
					isEdit: editable
				};
				if (editable) {
					updatedItem._prevProgramTypeId = item.programTypeId ?? null;
					updatedItem._prevProgramType = item.programType ?? null;
				}
				return updatedItem;
			}
			return item;
		});
		setOrderStyles(updatedArray);
	};

	const onCancelEdit = (id) => {
		const updatedArray = orderStyles.map((item) => {
			if (item.id === id) {
				const { _prevProgramTypeId, _prevProgramType, ...rest } = item;
				return {
					...rest,
					isEdit: false,
					programTypeId: _prevProgramTypeId !== undefined ? _prevProgramTypeId : item.programTypeId,
					programType: _prevProgramType !== undefined ? _prevProgramType : item.programType
				};
			}
			return item;
		});
		setOrderStyles(updatedArray);
		setGroupData(getGroupPoData(updatedArray));
	};

	const addEdt = async (rfqId) => {
		try {
			dispatch(openLoader());
			const data = await ApiClient.POST('duplicateRfq', '', `${rfqId}`);
			if (data?.status === 200) {
				toastSuccess('Success!');
				getOrderQuotes(orderId);
			} else {
				toastError('Unexpected response format.');
			}
		} catch (error) {
			dispatch(closeLoader());
			const errorMessage = error?.response?.data?.message || 'Request was unsuccessful.';
			toastError(errorMessage);
		} finally {
			dispatch(closeLoader());
		}
	};

	const onChangePO = (array, id, keyPath, value) => {
		const updatedArray = array.map((item) => {
			if (item.id === id) {
				const updatedItem = {
					...item,
					rfqId: id,
					[keyPath]: value
				};
				if (keyPath === 'deliveryDate' && !item?.prevDeliveryDate) {
					updatedItem['prevDeliveryDate'] = item.deliveryDate;
				}

				return updatedItem;
			}
			return item;
		});
		setOrderStyles(updatedArray);
		setGroupData(getGroupPoData(updatedArray));
		return updatedArray;
	};

	const createNewProgramType = async (name, setIsOpen) => {
		const trimmedName = String(name || '').trim();
		if (!trimmedName) return;

		try {
			setIsProgramTypeSubmitting(true);
			await createProgramType({ name: trimmedName }).unwrap();
			await fetchProgramTypes();
			toastSuccess('Program type created successfully');
			if (typeof setIsOpen === 'function') {
				setIsOpen(false);
			}
		} catch (error) {
			toastError(error?.data?.message || 'Failed to create program type.');
		} finally {
			setIsProgramTypeSubmitting(false);
		}
	};

	const onProgramTypeSelect = (singlePo, programType) => {
		const updatedArray = orderStyles.map((item) => {
			if (item.id === singlePo.id) {
				return {
					...item,
					programTypeId: programType?.id || null,
					programType: programType
						? {
								id: programType.id,
								name: programType.name
							}
						: null
				};
			}
			return item;
		});

		setOrderStyles(updatedArray);
		setGroupData(getGroupPoData(updatedArray));
	};

	const renderProgramTypeOptions = () => {
		return programTypeOptions.map((programType) => ({
			...programType,
			value: programType.name
		}));
	};

	const onProgramTypeEdit = (programType) => {
		setSelectedProgramType(programType);
		setProgramTypeName(programType?.name || '');
		setShowProgramTypeModal(true);
	};

	const closeProgramTypeModal = () => {
		setShowProgramTypeModal(false);
		setSelectedProgramType(null);
		setProgramTypeName('');
	};

	const onUpdateProgramType = async () => {
		const trimmedName = String(programTypeName || '').trim();
		if (!trimmedName || !selectedProgramType?.id) return;

		try {
			setIsProgramTypeSubmitting(true);
			await updateProgramType({ id: selectedProgramType.id, name: trimmedName }).unwrap();
			await fetchProgramTypes();
			setOrderStyles((prev) =>
				prev.map((item) =>
					item?.programTypeId === selectedProgramType.id
						? {
								...item,
								programType: {
									...(item?.programType || {}),
									id: selectedProgramType.id,
									name: trimmedName
								}
							}
						: item
				)
			);
			toastSuccess('Program type updated successfully');
			closeProgramTypeModal();
		} catch (error) {
			toastError(error?.data?.message || 'Failed to update program type.');
		} finally {
			setIsProgramTypeSubmitting(false);
		}
	};

	const handleFocus = (index) => {
		// Focus the input field at the given index
		if (inputRefs.current[index]) {
			inputRefs.current[index].focus();
		}
	};
	const getBoolVal = async (finalPayload) => {
		let isValid = true; // Default to true

		finalPayload.forEach((item) => {
			if (!isValid) return; // Exit early if already invalid

			if (item.buyerQuotationType === 'COLORWISE') {
				if (
					!item.colorWiseBuyerPrice || // Check if object exists
					Object.keys(item.colorWiseBuyerPrice).length !== item.colorWiseSizeQuantityPairList.length
				) {
					isValid = false;
				}
			} else if (item.buyerQuotationType === 'SIZEWISE') {
				if (
					!item.sizeWiseBuyerPrice || // Check if object exists
					!item.colorWiseSizeQuantityPairList || // Check if array exists
					!item.colorWiseSizeQuantityPairList[0]?.sizeQuantityPairList || // Check if nested array exists
					Object.keys(item.sizeWiseBuyerPrice).length !==
						item.colorWiseSizeQuantityPairList[0]?.sizeQuantityPairList.length
				) {
					isValid = false;
				}
			} else if (item.buyerQuotationType === 'DESIGNWISE') {
				if (!item.designWiseBuyerPrice) {
					isValid = false;
				}
			} else {
				isValid = false;
			}
		});

		return isValid;
	};

	const isPoNumberExist = (productId, rfqId) => {
		let selectedProduct = orderStyles.find((item) => item.id === rfqId);
		if (selectedProduct && !selectedProduct?.poNumber) {
			let errorObj = {
				[`po_${rfqId}`]: 'Po number is required'
			};
			setErrors(errorObj);
			return false;
		}
		setErrors({});
		return true;
	};

	const updateInfo = async (productId, rfqId, etdLogInfo = {}) => {
		if (!isPoNumberExist(productId, rfqId)) return;

		const finalPayload = orderStyles
			.filter((order) => order.productId === productId && order.id === rfqId)
			.map((order) => ({
				rfqId: order.id, // Assuming `order.id` represents `rfqId`
				...order, // Include all other fields from `order` if needed
				referenceNumber: order.productReferenceNumber,
				...etdLogInfo
			}));

		const hasNonZeroQuantity = finalPayload?.some((item) => {
			return item.colorWiseSizeQuantityPairList?.some((color) => {
				return color.sizeQuantityPairList?.some((size) => size.quantity > 0);
			});
		});

		const hasNonUnitPrice = await getBoolVal(finalPayload);

		if (hasNonZeroQuantity || hasNonUnitPrice) {
			setErrorDesign({});
		}
		if (!hasNonZeroQuantity) {
			//setError('Atleast one quantity is required');
			setErrorDesign({
				totalError: 'Atleast one quantity is required',
				designId: rfqId
			});
			return;
		}
		if (!hasNonUnitPrice) {
			// setError('Unit price is required');
			setErrorDesign({
				totalError: 'Unit price is required',
				designId: rfqId
			});
			return;
		}
		try {
			dispatch(openLoader());
			const { data } = await ApiClient.PUT('updateRfqInfo', finalPayload);

			// Handle successful response
			if (data?.success) {
				toastSuccess(data.message);
				getOrderQuotes(orderId);
				onSetEditIndex(rfqId, false);
				setError('');
				setProgramTypeErrors((prev) => {
					const updated = { ...prev };
					delete updated[rfqId];
					return updated;
				});
			} else {
				toastError('Unexpected response format.');
			}
		} catch (error) {
			dispatch(closeLoader());
			// Handle errors gracefully
			const errorMessage = error?.response?.data?.message || 'Request was unsuccessful.';
			toastError(errorMessage);
		} finally {
			dispatch(closeLoader());
		}
	};

	const deleteRfq = async (item) => {
		let ids = '';
		if (item?.isBaseRfq) {
			const matchingIds = orderStyles
				.filter((entry) => entry.productId === item.productId)
				.map((entry) => entry.id);
			ids = matchingIds.join(',');
		} else {
			ids += item?.id;
		}
		try {
			dispatch(openLoader());
			let url = 'deleteRfq';
			let params = `?ids=${ids}`;
			if (!isDrafted && !item.isBaseRfq) {
				url = 'deleteDuplicateRfq';
				params = `/${ids}`;
			}
			const { data } = await ApiClient.DELETE(url, '', params);
			if (data?.success) {
				toastSuccess(data.message);
				getOrderQuotes(orderId);
			} else {
				toastError('Unexpected response format.');
			}
		} catch (error) {
			dispatch(closeLoader());
			const errorMessage = error?.response?.data?.message || 'Request was unsuccessful.';
			toastError(errorMessage);
		} finally {
			dispatch(closeLoader());
		}
	};
	const onCloseViewModal = () => {
		dispatch(clearDesignViewModalInfo({}));
	};
	const openDesignViewModal = (item) => {
		dispatch(
			setDesignViewModalInfo({
				isShowMergeTechPack:
					item?.isSet || item?.setPackStyleFlag === STATIC_SET_CONSTANTS.SET_PRODUCT,
				id: item?.productId,
				onCloseViewModal: onCloseViewModal,
				source: 'PRODUCT_LIST',
				designIdList: designIdList
			})
		);
	};
	const handleRFQIds = (rfqId) => {
		if (selectedRFQIds.includes(rfqId)) {
			let tmpList = selectedRFQIds.filter((item) => item !== rfqId);
			setSelectedRFQIds(tmpList);
		} else {
			setSelectedRFQIds((prev) => [...prev, rfqId]);
		}
	};
	const filterStylesData = () => {
		if (hideStatusType) {
			return orderStyles.filter((item) => {
				switch (hideStatusType) {
					case 'piCreated':
						return item.isPiCreated === true;
					case 'piPending':
						return item.isPiCreated === false;
					case 'scCreated':
						return item.isScCreated === true;
					case 'scPending':
						return item.isScCreated === false;
					case 'tnaCreated':
						return item.isTNACreated === true;
					case 'tnaPending':
						return item.isTNACreated === false;
					default:
						return true;
				}
			});
		} else {
			return orderStyles;
		}
	};
	const isDisabledSubmitButton = (array) => {
		return array.some((item) => item.draftStatus !== 'COMPLETED' || item.isEdit);
	};
	const handleClose = () => {
		setShow(false);
	};
	if (!groupData) {
		// Show a loading spinner or placeholder while data is being initialized
		return <div>Loading...</div>;
	}

	const renderRightSidePanel = () => {
		return (
			<DesignsByCollection
				addNewStyle={addNewStyle}
				stylesData={productIds}
				setOpenStyleModal={setOpenStyleModal}
				collectionId={orderDetailsInfo.collectionId}
				folderId={orderDetailsInfo.folderId}
				requestFrom={'ORDER'}
			/>
		);
	};

	const submitETDLog = async (postData) => {
		await updateInfo(selectedPo.productId, selectedPo.id, postData);
		setSelectedPo(null);
	};

	return (
		<>
			{!isDrafted && (
				<div className="info-and-button d-flex justify-content-between align-items-center pt-4 pb-4">
					<p className="medium-14 gray_dark_01">
						Designs info{' '}
						<span className="regular-14 gray_dark_02 ml-2">
							(Total no of style - {orderStyles?.length || 0})
						</span>
					</p>
					<div>
						<button
							className="button outline size32"
							onClick={() => setOpenOrderDesignModal(true)}
						>
							<span className="ml-1">{stylesCount} Design</span>
						</button>
						<button
							className="button outline size32 ml-2"
							data-toggle="modal"
							data-target="#add-multiple-design"
							ref={buttonRef}
							onClick={() => setOpenStyleModal(true)}
						>
							<CommonImage src="/icons/plus-20.svg" alt="" />
							<span className="ml-1">Style</span>
						</button>
					</div>
				</div>
			)}

			{orderCancelInfo && Object.keys(orderCancelInfo).length > 0 && (
				<OrderCancelInfo orderCancelInfo={orderCancelInfo} getOrderCancelInfo={getOrderCancelInfo} />
			)}

			<>
				<div className="accordion">
					{groupData?.map((item, index) => (
						<div
							className={`card ${isOverflow ? 'overflow-visible' : ''} ${index === groupData.length - 1 ? 'last-card' : ''} ${!isDisabledSubmitButton(item?.poData) ? 'active' : ''}`}
							key={`card_${index}`}
						>
							<div className={`collapse show`}>
								{item?.poData.map((singlePo, index) => (
									<div
										key={singlePo.id}
										className={`card-body border-0 designs-info-section radius-6 ${singlePo.draftStatus === 'COMPLETED' && isDisabledSubmitButton(item?.poData) ? 'active' : ''} ${
											orderCancelInfo?.requestType === 'PRODUCT' &&
											orderCancelInfo?.status === 'REQUESTED' &&
											singlePo.canceledStatus === 'REQUESTED'
												? 'highlight-row'
												: ''
										}`}
									>
										<div className="designs-row order-desing-row">
											<div className={`single-design p-0 m-0 border-0`}>
												<div className="single-design-card with-editable">
													<div className="card-top">
														<div className="edit-items">
															<div className="select-and-edit d-flex justify-content-between">
																{!isDrafted &&
																	(singlePo?.status === 'ORDER_PLACED' ||
																		singlePo?.status === 'PENDING' ||
																		singlePo?.status === 'PRODUCT_SOLD' ||
																		singlePo?.status === 'RUNNING') && (
																		<div className="custom-chekbox">
																			<div className="form-group">
																				<input
																					type="checkbox"
																					id={`stage_${singlePo.id}`}
																					checked={selectedRFQIds.includes(
																						singlePo.id
																					)}
																					onChange={() =>
																						handleRFQIds(
																							singlePo.id
																						)
																					}
																				/>
																				<label
																					htmlFor={`stage_${singlePo.id}`}
																				></label>
																			</div>
																		</div>
																	)}
															</div>
														</div>
														<>
															{singlePo?.isSet ? (
																<div
																	onClick={() =>
																		openDesignViewModal(singlePo)
																	}
																>
																	{renderSetImages(
																		singlePo?.documentResponseList,
																		'100%'
																	)}
																</div>
															) : (
																<CommonImage
																	src={
																		singlePo?.documentResponseList[0]
																			?.docUrl
																	}
																	alt={singlePo.name}
																	isStatic={false}
																	onClick={() =>
																		openDesignViewModal(singlePo)
																	}
																/>
															)}
															<div className="order-status d-flex justify-content-between">
																{singlePo?.isSet && setTag()}
																{singlePo?.isPack && packTag()}
																{!singlePo?.isBaseRfq && splitTag()}
															</div>
														</>
													</div>
												</div>
												<div className="title-and-form mb-5">
													<div
														className={`d-flex justify-content-between align-items-center`}
													>
														<div className="d-flex align-items-center">
															<p className="medium-16 gray_dark_01 mr-3">
																{singlePo.isBaseRfq && (
																	<>
																		{item.productReferenceNumber}{' '}
																		<span className="regular-12 gray_dark_02">
																			({item?.name})
																		</span>
																	</>
																)}
															</p>
															<div className="pending-order-stepper on-design-info-item">
																{!isDrafted && (
																	<ul className="d-flex">
																		<li
																			className={
																				singlePo.isPiCreated
																					? ''
																					: 'disabled'
																			}
																		>
																			<span className="step-count done">
																				<CommonImage
																					src="/icons/tick-green.png"
																					alt="tich"
																				/>
																			</span>
																			PI
																		</li>
																		<li
																			className={
																				singlePo.isScCreated
																					? ''
																					: 'disabled'
																			}
																		>
																			<span className="step-count done">
																				<CommonImage
																					src="/icons/tick-green.png"
																					alt="tich"
																				/>
																			</span>
																			Sales contact
																		</li>
																		<li
																			className={
																				singlePo.isTNACreated
																					? ''
																					: 'disabled'
																			}
																		>
																			<span className="step-count done">
																				<CommonImage
																					src="/icons/tick-green.png"
																					alt="tich"
																				/>
																			</span>
																			TNA
																		</li>
																	</ul>
																)}
															</div>
														</div>
														<div className="edit-and-update-btn  d-flex justify-content-center">
															{singlePo.isEdit && (
																<>
																	<button
																		type="button"
																		className="button text size32 mr-2 px-0"
																		onClick={() =>
																			onCancelEdit(singlePo.id)
																		}
																	>
																		Cancel
																	</button>
																	<button
																		type="button"
																		className="button text size32 mr-2 px-0"
																		onClick={() => {
																			if (
																				!isDrafted &&
																				singlePo?.prevDeliveryDate &&
																				singlePo.deliveryDate !==
																					singlePo.prevDeliveryDate
																			) {
																				setSelectedPo({
																					productId:
																						singlePo.productId,
																					id: singlePo.id
																				});
																			} else {
																				updateInfo(
																					singlePo.productId,
																					singlePo.id
																				);
																			}
																		}}
																	>
																		<span className="blue">Update</span>
																	</button>
																</>
															)}
															<div className="option">
																<div className="dropdown">
																	<button
																		className="btn btn-default dropdown-toggle"
																		type="button"
																		id="menu1"
																		data-toggle="dropdown"
																		aria-expanded="false"
																	>
																		<CommonImage
																			src="/icons/more_vertical.svg"
																			alt=""
																		/>
																	</button>
																	<ul
																		className="dropdown-menu dropdown-menu-right shadow-lg"
																		role="menu"
																		aria-labelledby="menu1"
																		x-placement="bottom-end"
																	>
																		<li
																			role="presentation"
																			className="px-4 pb-3 pt-3"
																		>
																			<a
																				role="menuitem"
																				tabindex="-1"
																				className="font-weight-normal text-black"
																				//href="/invoices/edit/10052"
																				onClick={() =>
																					onSetEditIndex(
																						singlePo.id,
																						true
																					)
																				}
																			>
																				Edit
																			</a>
																		</li>
																		<li
																			role="presentation"
																			className="px-4 pb-3 pt-3"
																		>
																			<a
																				role="menuitem"
																				tabindex="-1"
																				className="font-weight-normal  text-black"
																				onClick={() =>
																					addEdt(singlePo?.id)
																				}
																			>
																				Add ETD/PO
																			</a>
																		</li>
																		{(isDrafted ||
																			(!isDrafted &&
																				!singlePo.isBaseRfq)) && (
																			<li
																				role="presentation"
																				className="px-4 pb-3"
																			>
																				<a
																					role="menuitem"
																					tabindex="-1"
																					className="font-weight-normal  text-black"
																					onClick={() =>
																						deleteRfq(singlePo)
																					}
																				>
																					Delete
																				</a>
																			</li>
																		)}
																	</ul>
																</div>
															</div>
														</div>
													</div>
													{/* {!singlePo.isBaseRfq && singlePo.isEdit && (
														<div className="split-order-form d-block">
															<div className="single-design-input">
																<div className="row">
																	<div className="col-md-4">
																		<div className="form-group mb-2">
																			<label htmlFor="po-no">
																				PO No
																			</label>
																			<input
																				type="text"
																				id=""
																				placeholder="PO No"
																				value={singlePo.poNumber}
																				onChange={(e) =>
																					onChangePO(
																						orderStyles,
																						singlePo.id,
																						'poNumber',
																						e.target.value
																					)
																				}
																				disabled={!singlePo.isEdit}
																			/>
																			{errors?.[
																				`po_${singlePo.id}`
																			] && (
																				<p className="error">
																					{
																						errors?.[
																							`po_${singlePo.id}`
																						]
																					}
																				</p>
																			)}
																		</div>
																	</div>
																	<div className="col-md-4 ">
																		<div className="form-group mb-2">
																			<label htmlFor="design-no">
																				PO received date
																			</label>
																			<input
																				type="date"
																				placeholder="02/05/2024"
																				id=""
																				value={singlePo.poReceiveDate}
																				onChange={(e) =>
																					onChangePO(
																						orderStyles,
																						singlePo.id,
																						'poReceiveDate',
																						e.target.value
																					)
																				}
																				disabled={!singlePo.isEdit}
																			/>
																		</div>
																	</div>
																	<div className="col-md-4">
																		<div className="form-group mb-2">
																			<label htmlFor="po-no">
																				ETD (dd/mm/yyyy)
																			</label>
																			<input
																				type="date"
																				id=""
																				placeholder="02/05/2024"
																				value={singlePo.deliveryDate}
																				onChange={(e) =>
																					onChangePO(
																						orderStyles,
																						singlePo.id,
																						'deliveryDate',
																						e.target.value
																					)
																				}
																				disabled={!singlePo.isEdit}
																			/>
																		</div>
																	</div>
																</div>
															</div>
														</div>
													)} */}
													{!singlePo.isEdit && (
														<div className="desing-all-content d-flex">
															<div className="design-details-info pt-3 ml-0">
																<div className="single-design-view">
																	<div className="content">
																		<p className="regular-14 gray_dark_02 mb-12">
																			PO NO:{' '}
																			<span className="gray_dark_01">
																				{' '}
																				{singlePo.poNumber}
																			</span>
																		</p>
																		<p className="regular-14 gray_dark_02 mb-12">
																			PO receive date:{' '}
																			<span className="gray_dark_01">
																				{singlePo.poReceiveDate
																					? moment(
																							singlePo.poReceiveDate
																						).format(
																							'DD-MMM-YYYY'
																						)
																					: ''}
																			</span>
																		</p>
																	</div>
																</div>
															</div>
															<div className="design-details-info pt-3">
																<div className="single-design-view">
																	<div className="content">
																		<p className="regular-14 gray_dark_02 mb-12">
																			Supplier:
																			<span className="gray_dark_01">
																				{' '}
																				{singlePo?.supplierName}
																			</span>
																		</p>
																		<div className="buyer-etd-and-timeline d-flex align-items-center">
																			<p className="regular-14 gray_dark_02 ">
																				Buyer ETD:{' '}
																				<span className="gray_dark_01">
																					{moment(
																						singlePo.deliveryDate
																					).format('DD-MMM-YYYY')}
																				</span>
																			</p>
																			{!isDrafted &&
																				singlePo
																					?.deliveryDateChangeResponseList
																					?.length > 0 && (
																					<div className="buyer-timeling position-relative">
																						<p
																							className="edited
																					regular-12 gray_dark_02
																					ml-2 cursor-pointer"
																						>
																							Edited
																						</p>
																						<EtdUpdateTimeline
																							timelineData={
																								singlePo.deliveryDateChangeResponseList
																							}
																						/>
																					</div>
																				)}
																		</div>
																	</div>
																</div>
															</div>
															<div className="design-details-info pt-3">
																<div className="single-design-view">
																	<div className="content">
																		<p className="regular-14 gray_dark_02 mb-12">
																			PO Qty:{' '}
																			<span className="gray_dark_01">
																				{singlePo?.total ||
																					singlePo?.quantity}
																			</span>
																		</p>

																		<p className="regular-14 gray_dark_02">
																			PO Amount:{' '}
																			<span className="gray_dark_01">
																				${singlePo?.amount}
																			</span>
																		</p>
																	</div>
																</div>
															</div>
															<div className="design-details-info">
																<div className="single-design-view">
																	<div className="content pt-3">
																		{singlePo?.unitOfMeasurementQty && (
																			<p className="regular-14 gray_dark_02 mb-12">
																				{singlePo?.unitOfMeasurementType ===
																				'SET_STYLE'
																					? 'SET'
																					: singlePo?.unitOfMeasurementType ===
																						  'PACK_STYLE'
																						? 'PACK'
																						: 'Set/Pack'}
																				:{' '}
																				<span className="gray_dark_01">
																					{
																						singlePo?.unitOfMeasurementQty
																					}{' '}
																					Pcs
																				</span>
																			</p>
																		)}

																		<p className="regular-14 gray_dark_02">
																			Program Type:{' '}
																			<span className="gray_dark_01">
																				{singlePo?.programType?.name}
																			</span>
																		</p>
																		{programTypeErrors?.[singlePo.id] && (
																			<p className="error regular-12">
																				{
																					programTypeErrors[
																						singlePo.id
																					]
																				}
																			</p>
																		)}
																	</div>
																</div>
															</div>
														</div>
													)}
													{/* {singlePo.isEdit ? ( */}
													{singlePo.isEdit && (
														<div className="split-order-form pt-2">
															<div className="single-design-input">
																<div className="row">
																	<div className="col-md-12">
																		<div className="form-group mb-2">
																			<label htmlFor="design-no">
																				Design No
																			</label>
																			<input
																				ref={(el) =>
																					(inputRefs.current[
																						index
																					] = el)
																				}
																				type="text"
																				placeholder="Design No"
																				name="productReferenceNumber"
																				id={`productReferenceNumber_${singlePo.id}`}
																				value={
																					singlePo.productReferenceNumber
																				}
																				onChange={(e) =>
																					onChangePO(
																						orderStyles,
																						singlePo.id,
																						'productReferenceNumber',
																						e.target.value
																					)
																				}
																				onFocus={() =>
																					handleFocus(index)
																				}
																				disabled={!singlePo.isEdit}
																			/>
																		</div>
																	</div>
																	<div className="col-md-12">
																		<div className="form-group mb-2">
																			<label htmlFor="po-no">
																				PO No
																			</label>
																			<input
																				type="text"
																				id=""
																				placeholder="PO No"
																				value={singlePo.poNumber}
																				onChange={(e) =>
																					onChangePO(
																						orderStyles,
																						singlePo.id,
																						'poNumber',
																						e.target.value
																					)
																				}
																				disabled={!singlePo.isEdit}
																			/>
																			{errors?.[
																				`po_${singlePo.id}`
																			] && (
																				<p className="error">
																					{
																						errors?.[
																							`po_${singlePo.id}`
																						]
																					}
																				</p>
																			)}
																		</div>
																	</div>
																</div>
															</div>
															<div className="single-design-input">
																<div className="row">
																	<div className="col-md-12">
																		<div className="form-group mb-2">
																			<label htmlFor="design-no">
																				PO received date
																			</label>
																			<input
																				type="date"
																				placeholder="02/05/2024"
																				id=""
																				value={singlePo.poReceiveDate}
																				onChange={(e) =>
																					onChangePO(
																						orderStyles,
																						singlePo.id,
																						'poReceiveDate',
																						e.target.value
																					)
																				}
																				disabled={!singlePo.isEdit}
																			/>
																		</div>
																	</div>
																	<div className="col-md-12">
																		<div className="form-group mb-2">
																			<label htmlFor="po-no">
																				ETD (mm/dd/yyyy)
																			</label>
																			<input
																				type="date"
																				id=""
																				placeholder="02/05/2024"
																				value={singlePo.deliveryDate}
																				onChange={(e) =>
																					onChangePO(
																						orderStyles,
																						singlePo.id,
																						'deliveryDate',
																						e.target.value
																					)
																				}
																				disabled={!singlePo.isEdit}
																			/>
																		</div>
																	</div>
																</div>
															</div>
															<div className="single-design-input">
																<div className="row">
																	<div className="col-md-12">
																		<div className="form-group composition-item mb-0">
																			<label>UOM</label>
																			<div className="d-flex composition-and-qty">
																				<input
																					type="text"
																					className="input-percentage"
																					placeholder={
																						singlePo?.isPack
																							? 'Pack'
																							: singlePo?.isSet
																								? 'Set'
																								: 'Set/Pack'
																					}
																					onChange={(e) =>
																						onChangePO(
																							orderStyles,
																							singlePo.id,
																							'unitOfMeasurementType',
																							e.target.value
																						)
																					}
																					value={
																						singlePo?.isPack
																							? 'Pack'
																							: singlePo?.isSet
																								? 'Set'
																								: 'Set/Pack'
																					}
																					disabled={true}
																				/>

																				<div className="select-component">
																					<div className="form-group size36 mb-2">
																						<div className="select-component">
																							<input
																								type="number"
																								onKeyPress={
																									validateNumber
																								}
																								className="w-100"
																								placeholder="Write qty"
																								onChange={(
																									e
																								) =>
																									onChangePO(
																										orderStyles,
																										singlePo.id,
																										'unitOfMeasurementQty',
																										e
																											.target
																											.value
																									)
																								}
																								disabled={
																									singleSetPackStyle !==
																										'PACK_STYLE' &&
																									!singlePo.isEdit
																								}
																								value={
																									singlePo.unitOfMeasurementQty
																								}
																							/>
																						</div>
																					</div>
																				</div>
																			</div>
																		</div>
																	</div>
																	<div className="col-md-12">
																		<div className="form-group composition-item mb-0">
																			<label>Program Type</label>
																			<div className="d-flex composition-and-qty">
																				<div className="w-100">
																					<CustomDropdown
																						title="Select program type"
																						isShowLabel={true}
																						isShowSearch={true}
																						isDisabled={
																							!singlePo.isEdit
																						}
																						items={renderProgramTypeOptions()}
																						onAddNewButtonClick={
																							createNewProgramType
																						}
																						showAddNewButton={isAdmin()}
																						selectedItem={
																							(
																								singlePo?.programType ||
																								programTypeOptions.find(
																									(item) =>
																										item.id ===
																										singlePo?.programTypeId
																								) ||
																								{}
																							).name || ''
																						}
																						isEditable={isAdmin()}
																						onEdit={
																							onProgramTypeEdit
																						}
																						onItemClick={(
																							value
																						) =>
																							onProgramTypeSelect(
																								singlePo,
																								value
																							)
																						}
																					/>
																				</div>
																			</div>
																		</div>
																	</div>
																</div>
															</div>
														</div>
													)}
												</div>
											</div>
											<ColorSizeBreakDownTable
												item={singlePo}
												stylesData={orderStyles}
												setOrderStyles={setOrderStyles}
												quoteId={singlePo.id}
												isDrafted={isDrafted}
												error={error}
												errorDesign={errorDesign}
												//isOverflow={isOverflow}
												setIsOverflow={setIsOverflow}
												poItemIdentifier={poItemIdentifier}
												//isDisabled={!item?.isBaseRfq || !item.isEdit}
												// isDisabledTable={!item.isEdit}
												pOSegmentationType={pOSegmentationType}
											/>
										</div>
									</div>
								))}
							</div>
						</div>
					))}

					{openStyleModal && renderRightSidePanel()}
					<div className="footer-button d-flex justify-content-end">
						{isDrafted && (
							<button
								className="button outline size32"
								data-toggle="modal"
								data-target="#add-multiple-design"
								ref={buttonRef}
								onClick={() => setOpenStyleModal(true)}
							>
								<CommonImage src="/icons/plus-20.svg" alt="" />
								<span className="ml-1">Style</span>
							</button>
						)}
						<button
							className={'button gray-outline size32 ml-2 d-none'}
							data-toggle="modal"
							data-target="#add-multiple-design"
							ref={buttonRef}
							onClick={() => setOpenStyleModal(true)}
						>
							<img src={`${process.env.PUBLIC_URL}/icons/plus-20.svg`} alt="add" />
							<span className="ml-1 gray_dark_01">Style</span>
						</button>
						{isDrafted && (
							<button
								disabled={isDisabledSubmitButton(orderStyles)}
								className="button size32 ml-2"
								onClick={onSave}
							>
								Submit
							</button>
						)}
					</div>
				</div>
				<OrderCancelBottomBar selectedRFQIds={selectedRFQIds} setSelectedRFQIds={setSelectedRFQIds} />
				{/* <AddStyleFromExistingOrder
					newlyAddedStyle={newlyAddedStyles}
					orderId={orderId}
					onAddSuccess={onAddSuccess}
					onRemoveStyles={onRemoveStyles}
				/> */}
			</>
			<PlaceOrderConfirmation
				show={show}
				onPlaceOrder={createOrder}
				orderStyles={orderStyles}
				handleClose={handleClose}
				isPlacingOrder={isPlacingOrder}
			/>
			{showProgramTypeModal && (
				<ModalContainer onCloseModal={closeProgramTypeModal}>
					<div className="common-popup shadow-24dp">
						<div className="common-popup-header d-flex justify-content-between">
							<div className="popup-tilte">
								<h3 className="semibold-16 mb-0">Update program type</h3>
							</div>
							<div className="close-btn" onClick={closeProgramTypeModal}>
								<img src={`${process.env.PUBLIC_URL}/icons/close.svg`} alt="close" />
							</div>
						</div>
						<div className="common-popup-body">
							<div className="form-group mb-0">
								<label htmlFor="programTypeName">Program type name</label>
								<input
									id="programTypeName"
									type="text"
									autoComplete="off"
									value={programTypeName}
									onChange={(e) => setProgramTypeName(e.target.value)}
									placeholder="Write program type name"
								/>
							</div>
						</div>
						<div className="modal-footer">
							<div className="footer-buttons text-right">
								<button
									className="button gray-outline size32"
									onClick={closeProgramTypeModal}
									disabled={isProgramTypeSubmitting}
								>
									Cancel
								</button>
								<button
									className="button size32 ml-2"
									onClick={onUpdateProgramType}
									disabled={!programTypeName.trim() || isProgramTypeSubmitting}
								>
									Update
								</button>
							</div>
						</div>
					</div>
				</ModalContainer>
			)}
			{selectedPo && <EtdUpdateLog onClose={() => setSelectedPo(null)} onSubmit={submitETDLog} />}
		</>
	);
};

export default OrderStyles;
